"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
const moment_timezone_1 = __importDefault(require("moment-timezone"));
const mxp_schemas_1 = require("mxp-schemas");
const isPhysicalProduct_1 = __importDefault(require("../product/isPhysicalProduct"));
const product_1 = require("../product");
exports.LINE_ITEM_ACCESS_START_DATE = 'access_start_datetime';
exports.LINE_ITEM_ACCESS_END_DATE = 'access_end_datetime';
exports.PRODUCT_ATTRIBUTE_START_DATE = 'startDateTime';
exports.PRODUCT_ATTRIBUTE_END_DATE = 'endDateTime';
exports.PRODUCT_ATTRIBUTE_AVAILABILITY = 'availability';
var AccessRetrievalMethod;
(function (AccessRetrievalMethod) {
    AccessRetrievalMethod[AccessRetrievalMethod["ForceCalculation"] = 0] = "ForceCalculation";
    AccessRetrievalMethod[AccessRetrievalMethod["CalculateOnlyIfDoesNotExist"] = 1] = "CalculateOnlyIfDoesNotExist";
})(AccessRetrievalMethod = exports.AccessRetrievalMethod || (exports.AccessRetrievalMethod = {}));
const DEFAULT_AVAILABILITY = 90;
exports.startOfDay = (date) => moment_timezone_1.default(new Date(date)).utc().startOf('day').toDate();
exports.endOfDay = (date) => moment_timezone_1.default(new Date(date)).utc().endOf('day').toDate();
/**
 * Product types that are eligible for stacking, only subscription
 */
const STACKABLE_PRODUCT_TYPES = [
    mxp_schemas_1.Product.ProductType.SUBSCRIPTION,
    mxp_schemas_1.Product.ProductType.PUBLICATION,
    mxp_schemas_1.Product.ProductType.FLP,
];
exports.isAutoRenewableProduct = (item) => item.autoRenew || item.productType === mxp_schemas_1.Product.ProductType.SUBSCRIPTION;
/**
 * Check of the line item or product is eligible for stacking; this is a very basic check.
 * Refer to is stacking candidate base and top for more details.
 * @param item
 */
exports.isStackable = (item) => {
    var _a;
    // Product Base or Cart.LineItem
    if ('productType' in item && typeof item.productType === 'string') {
        return STACKABLE_PRODUCT_TYPES.includes(item.productType);
    }
    // CommerceTypes.LineItem
    const productType = (_a = item.productType.obj) === null || _a === void 0 ? void 0 : _a.key;
    if (!productType) {
        throw new Error('Line item is not expanded properly');
    }
    return STACKABLE_PRODUCT_TYPES.includes(productType);
};
/**
 * Extracts embedded access_start_datetime and access_end_datetime within the line item of carts/orders
 * @param item
 */
exports.extractAccessDatesFromLineItem = (item) => {
    if (!item.custom || !item.custom.fields) {
        return {};
    }
    return {
        accessStartDateTime: item.custom.fields[exports.LINE_ITEM_ACCESS_START_DATE]
            ? new Date(item.custom.fields[exports.LINE_ITEM_ACCESS_START_DATE])
            : undefined,
        accessEndDateTime: item.custom.fields[exports.LINE_ITEM_ACCESS_END_DATE]
            ? new Date(item.custom.fields[exports.LINE_ITEM_ACCESS_END_DATE])
            : undefined,
    };
};
/**
 * Extracts a product (variant) related attribute from a cart (RAVE or CT) line item
 * @param name
 * @param item
 */
exports.extractAttribute = (name, item) => {
    var _a, _b, _c;
    if (Array.isArray((_a = item.variant) === null || _a === void 0 ? void 0 : _a.attributes)) {
        const foundAttribute = (((_b = item.variant) === null || _b === void 0 ? void 0 : _b.attributes) || []).find(attribute => attribute.name === name);
        if (foundAttribute) {
            return foundAttribute.value;
        }
        return undefined;
    }
    return (((_c = item.variant) === null || _c === void 0 ? void 0 : _c.attributes) || {})[name];
};
/**
 * Extracts the line item status from a line item (RAVE or Commerce Tools cart)
 * @param item
 */
exports.extractLineItemStatus = (item) => {
    if (!item.state.length || !item.state[0].state.obj) {
        return undefined;
    }
    return item.state[0].state.obj.key;
};
/**
 * Converts an array of orders to an array of line items
 * @param orders
 */
exports.flattenOrdersToLineItems = (orders) => orders.reduce((previousValue, order) => [...previousValue, ...order.lineItems], []);
exports.extractFixedStartDate = (item) => {
    if ('isMaster' in item) {
        return typeof item.startDateTime !== 'undefined' && item.startDateTime !== '' && item.startDateTime !== null
            ? new Date(item.startDateTime)
            : undefined;
    }
    const startDate = exports.extractAttribute(exports.PRODUCT_ATTRIBUTE_START_DATE, item);
    return typeof startDate !== 'undefined' && startDate !== '' && startDate !== null ? new Date(startDate) : undefined;
};
exports.extractFixedEndDate = (item) => {
    if ('isMaster' in item) {
        return typeof item.endDateTime !== 'undefined' && item.endDateTime !== '' && item.endDateTime !== null
            ? new Date(item.endDateTime)
            : undefined;
    }
    const endDate = exports.extractAttribute(exports.PRODUCT_ATTRIBUTE_END_DATE, item);
    return typeof endDate !== 'undefined' && endDate !== '' && endDate !== null ? new Date(endDate) : undefined;
};
/**
 * Test if a RAVE line item or product (about to be added to cart) is eligible to have its start date shifted
 * @param item
 */
exports.isStackingTopCandidate = (item) => exports.isStackable(item) && !Boolean(exports.extractFixedStartDate(item));
/**
 * Checks if a line item (legacy) is a candidate for being a base for stacking
 * @param lineItem
 */
const isStackingBaseCandidate = (lineItem) => {
    if (!exports.isStackable(lineItem)) {
        return false;
    }
    const lineItemStatus = exports.extractLineItemStatus(lineItem);
    if (lineItemStatus !== mxp_schemas_1.Orders.LineItemStates.LEGACYPROVISIONED &&
        lineItemStatus !== mxp_schemas_1.Orders.LineItemStates.PROVISIONED) {
        // The check if legacy or not is done as part of order searching
        // Not provisioned
        return false;
    }
    const { accessEndDateTime } = exports.extractAccessDatesFromLineItem(lineItem);
    if (!accessEndDateTime || accessEndDateTime.valueOf() < Date.now().valueOf()) {
        // The end date is in the past
        return false;
    }
    // Must be ok to stack :)
    return true;
};
/**
 * Filters a list of line items to the one eligible to be a stacking base: they must be: have an end date on the line item, active, from legacy, and match the item SKU
 * @param lineItems
 * @param itemSku
 */
exports.findStackingCandidates = (lineItems, itemSku) => lineItems.filter((lineItem) => {
    if (lineItem.variant.sku !== itemSku) {
        // Not the same product
        return false;
    }
    return isStackingBaseCandidate(lineItem);
});
/**
 * Finds the one with the latest end date
 * @param lineItems
 */
exports.findLatestLineItem = (lineItems) => lineItems.length
    ? lineItems.sort((lineItemA, lineItemB) => {
        const { accessEndDateTime: accessEndDateTimeA } = exports.extractAccessDatesFromLineItem(lineItemA);
        const { accessEndDateTime: accessEndDateTimeB } = exports.extractAccessDatesFromLineItem(lineItemB);
        return ((accessEndDateTimeB ? accessEndDateTimeB.valueOf() : 0) -
            (accessEndDateTimeA ? accessEndDateTimeA.valueOf() : 0));
    })[0]
    : undefined;
/**
 *
 * @param accessRetrievalMethod
 * @param item
 * @param orders
 */
exports.calculateStartDate = (accessRetrievalMethod, item, orders) => {
    const shouldStackItem = orders && orders.length && exports.isStackable(item);
    const itemSku = 'sku' in item ? item.sku : item.variant.sku;
    const stackingBaseCandidate = shouldStackItem
        ? exports.findLatestLineItem(exports.findStackingCandidates(exports.flattenOrdersToLineItems(orders), itemSku))
        : undefined;
    const calculatedStartDate = exports.startOfDay((stackingBaseCandidate && exports.extractAccessDatesFromLineItem(stackingBaseCandidate).accessEndDateTime) ||
        ('orderDate' in item && item.orderDate && new Date(item.orderDate)) ||
        new Date());
    let stackedAccessStartDateForFLP;
    if (exports.isFlpProduct(item) && orders && orders.length) {
        const latestLineItem = exports.findLatestLineItem(exports.flattenOrdersToLineItems(orders));
        const endDateOfPreviousOrder = exports.extractAccessDatesFromLineItem(latestLineItem).accessEndDateTime;
        const stackedAccessStartDate = moment_timezone_1.default(endDateOfPreviousOrder).utc().add(1, 'days').startOf('day').toDate();
        stackedAccessStartDateForFLP =
            stackedAccessStartDate < calculatedStartDate ? calculatedStartDate : stackedAccessStartDate;
    }
    const itemFixedDate = exports.extractFixedStartDate(item);
    /**
     * Its a product variant
     */
    if ('isMaster' in item) {
        return itemFixedDate || stackedAccessStartDateForFLP || calculatedStartDate;
    }
    /**
     * Its a line item
     */
    const { accessStartDateTime } = exports.extractAccessDatesFromLineItem(item);
    // Calculate the accessStartDateTime
    if (!accessStartDateTime || accessRetrievalMethod === AccessRetrievalMethod.ForceCalculation) {
        return itemFixedDate || stackedAccessStartDateForFLP || calculatedStartDate;
    }
    // Access Date is already set on the line item, just reuse it
    return accessStartDateTime;
};
/**
 *
 * @param accessRetrievalMethod
 * @param startDate
 * @param item
 */
exports.calculateEndDate = (accessRetrievalMethod, startDate, item) => {
    var _a;
    // eslint-disable-next-line no-param-reassign
    startDate = exports.endOfDay(startDate);
    const itemFixedEndDate = exports.extractFixedEndDate(item);
    const { accessEndDateTime } = exports.extractAccessDatesFromLineItem(item);
    const isFLPProductCheck = exports.isFlpProduct(item);
    if (isFLPProductCheck) {
        if (!accessEndDateTime || accessRetrievalMethod === AccessRetrievalMethod.ForceCalculation) {
            const flpTerm = exports.getFlpTerm(item, startDate);
            return flpTerm;
        }
        // Access Date is already set on the line item, just reuse it
        return accessEndDateTime;
    }
    /**
     * Its a product variant
     */
    if ('isMaster' in item) {
        const availability = (_a = item.accessAvailabilityInDays) !== null && _a !== void 0 ? _a : DEFAULT_AVAILABILITY;
        return moment_timezone_1.default(itemFixedEndDate || startDate)
            .add(availability, 'days')
            .toDate();
    }
    /**
     * Its a line item
     */
    // Calculate the accessEndDateTime
    if (!accessEndDateTime || accessRetrievalMethod === AccessRetrievalMethod.ForceCalculation) {
        const extractedAvailabilityValue = exports.extractAttribute(exports.PRODUCT_ATTRIBUTE_AVAILABILITY, item);
        const availability = Boolean(extractedAvailabilityValue) || extractedAvailabilityValue === 0
            ? extractedAvailabilityValue
            : DEFAULT_AVAILABILITY;
        return moment_timezone_1.default(itemFixedEndDate || startDate)
            .add(availability, 'days')
            .toDate();
    }
    return accessEndDateTime;
};
exports.isFlpProduct = (item) => {
    var _a, _b;
    const productType = typeof (item === null || item === void 0 ? void 0 : item.productType) === 'string' ? item.productType : (_b = (_a = item === null || item === void 0 ? void 0 : item.productType) === null || _a === void 0 ? void 0 : _a.obj) === null || _b === void 0 ? void 0 : _b.key;
    return productType === mxp_schemas_1.Product.ProductType.FLP;
};
exports.isFlpOrder = (order) => {
    return order.lineItems.some(item => exports.isFlpProduct(item));
};
function hasFlpTerm(item) {
    return 'flpTerm' in item;
}
function hasVariant(item) {
    var _a;
    return 'variant' in item && ((_a = item.variant) === null || _a === void 0 ? void 0 : _a.attributes);
}
exports.getFlpTerm = (item, startDate) => {
    var _a, _b;
    let flpTerm = '12';
    if (typeof (item === null || item === void 0 ? void 0 : item.productType) === 'string') {
        if (hasFlpTerm(item)) {
            flpTerm = ((_a = item.flpTerm) === null || _a === void 0 ? void 0 : _a.key) || '12';
        }
    }
    else if (hasVariant(item)) {
        const flpTermAttribute = ((_b = item.variant.attributes.find((a) => a.name === 'flpTerm')) === null || _b === void 0 ? void 0 : _b.value) || {};
        flpTerm = (flpTermAttribute === null || flpTermAttribute === void 0 ? void 0 : flpTermAttribute.key) || '12';
    }
    return moment_timezone_1.default(startDate)
        .utc()
        .add(flpTerm || '12', 'M')
        .subtract(1, 'days')
        .endOf('day')
        .toDate();
};
/**
 * Check if we should calculate access data for this line item. (i.e. do not if physical product)
 * @param item
 */
exports.shouldCalculateAccess = (item) => !(('isPhysicalProduct' in item
    ? item.isPhysicalProduct
    : isPhysicalProduct_1.default(item.variant.attributes).isPhProduct) ||
    'membershipMonthStart' in item ||
    product_1.isSectionProduct(item) ||
    product_1.isCredentialProduct(item) ||
    product_1.isMembershipProduct(item) ||
    product_1.isCenterMembershipProduct(item));
exports.convertToISODateOnlyString = (date) => date === null || date === void 0 ? void 0 : date.toISOString().split('T')[0];
//# sourceMappingURL=index.js.map